"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useQueue } from "@uidotdev/usehooks";
import {
  resolveSimulationTypeFromPath,
  uploadAudioBlobToS3,
} from "@/lib/simulation-audio-upload";
import { enqueuePendingSimulationAudio } from "@/lib/pending-simulation-audio";
import { coalesceMicrophoneStart } from "@/lib/microphone/start-coalescing";
import { copyAudioBufferView } from "@/lib/assembly/turnLifecycle";

const MicrophoneContext = createContext({});

// Custom hook for media stream management
function useMediaStream() {
  const streamRef = useRef(null);
  const tracksRef = useRef(new Set());

  // Memoize getStream so it's a stable function reference.
  const getStream = useCallback(() => streamRef.current, []);

  const setStream = useCallback((newStream) => {
    // Stop and clear previous tracks.
    tracksRef.current.forEach((track) => {
      track.stop();
    });
    tracksRef.current.clear();

    if (newStream) {
      newStream.getTracks().forEach((track) => {
        tracksRef.current.add(track);
        track.addEventListener("ended", () => {
          tracksRef.current.delete(track);
        });
      });
    }
    streamRef.current = newStream;
  }, []);

  const stopAllTracks = useCallback(() => {
    tracksRef.current.forEach((track) => {
      try {
        track.stop();
        track.enabled = false;
      } catch (e) {
        console.error(
          "[Microphone] Error stopping track:",
          track.kind,
          track.label,
          e
        );
      }
    });
    tracksRef.current.clear();
  }, []);

  return { getStream, setStream, stopAllTracks };
}

// Custom hook for media recorder management
function useMediaRecorder() {
  const recorderRef = useRef(null);

  const startRecording = useCallback(
    (stream, timeSlice, options, onDataAvailable) => {
      // Stop any existing recorder before starting a new one.
      stopRecording();

      try {
        const recorder = new MediaRecorder(stream, options);
        recorder.ondataavailable = onDataAvailable;
        recorder.start(timeSlice);
        recorderRef.current = recorder;
        return recorder;
      } catch (error) {
        console.error("MediaRecorder error:", error);
        return null;
      }
    },
    []
  );

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
      recorderRef.current.ondataavailable = null;
    }
    recorderRef.current = null;
  }, []);

  return {
    startRecording,
    stopRecording,
    getRecorder: () => recorderRef.current,
  };
}

const MicrophoneContextProvider = ({ children }) => {
  const { getStream, setStream, stopAllTracks } = useMediaStream();
  const { startRecording: startConvRecorder, stopRecording: stopConvRecorder } =
    useMediaRecorder();

  const [microphoneOpen, setMicrophoneOpen] = useState(false);
  // Keep an up-to-date ref for callbacks.
  const microphoneOpenRef = useRef(microphoneOpen);
  useEffect(() => {
    microphoneOpenRef.current = microphoneOpen;
  }, [microphoneOpen]);

  const [isInitializing, setIsInitializing] = useState(false);
  const isInitializingRef = useRef(false);
  const [isRecordingConversation, setIsRecordingConversation] = useState(false);
  const [chosenMimeType, setChosenMimeType] = useState(null);
  const [conversationChunks, setConversationChunks] = useState([]);
  const [microphoneDeviceName, setMicrophoneDeviceName] = useState(null);

  const mergeBuffers = useCallback((lhs, rhs) => {
    const mergedBuffer = new Int16Array(lhs.length + rhs.length);
    mergedBuffer.set(lhs, 0);
    mergedBuffer.set(rhs, lhs.length);
    return mergedBuffer;
  }, []);

  // AudioContext and systemSource are persisted on the shared audio element to
  // survive component unmount/remount cycles. createMediaElementSource can only
  // be called ONCE per element; closing the AudioContext orphans the capture and
  // leaves the element unable to produce audible output.
  const audioContextRef = useRef(null);
  const systemSourceRef = useRef(null);
  const streamSourceRef = useRef(null);
  const micGainRef = useRef(null);
  const conversationDestinationRef = useRef(null);
  const workletLoadedRef = useRef(false);

  // On mount, recover a persisted AudioContext/systemSource from the shared
  // audio element so we never call createMediaElementSource twice.
  // Also resume a suspended context so TTS audio is audible immediately
  // (before startMicrophone is called).
  useEffect(() => {
    const el = document.getElementById("shared-audio-player");
    if (!el) return;
    if (el.__persistedAudioCtx && el.__persistedAudioCtx.state !== "closed") {
      audioContextRef.current = el.__persistedAudioCtx;
      systemSourceRef.current = el.__persistedSystemSource;
      workletLoadedRef.current = el.__persistedWorkletLoaded || false;

      if (el.__persistedAudioCtx.state === "suspended") {
        el.__persistedAudioCtx.resume().catch((err) => {
          console.warn("[Microphone] Error resuming persisted audio context:", err);
        });
      }
    }
  }, []);
  const audioWorkletNodeRef = useRef(null);
  const audioBufferQueueRef = useRef(new Int16Array(0));
  const audioDebugRef = useRef({
    firstAudioLogged: false,
    lastAudioAt: 0,
    lastChunkLogAt: 0,
    lastNoDataLogAt: 0,
    noAudioTimeoutId: null,
    chunkCount: 0,
  });
  const {
    add: enqueueBlob,
    remove: removeBlob,
    clear: clearQueue,
    first: firstBlob,
    size: queueSize,
    queue,
  } = useQueue([]);

  // IMPORTANT: do not include `queue` in any cleanup callback dependencies.
  // `useQueue` updates on every audio chunk; if cleanup depends on `queue`, the
  // cleanup function identity changes and any `useEffect([cleanup])` teardown can
  // run repeatedly during recording, stopping the microphone unexpectedly.
  const queueRef = useRef(queue);
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const clearQueuedAudio = useCallback(() => {
    audioBufferQueueRef.current = new Int16Array(0);
    queueRef.current = [];
    clearQueue();
  }, [clearQueue]);

  const isRecoverableMicrophoneConstraintError = useCallback((error) => {
    return [
      "OverconstrainedError",
      "ConstraintNotSatisfiedError",
      "NotFoundError",
    ].includes(error?.name);
  }, []);

  const buildAudioConstraints = useCallback((deviceId = null, minimal = false) => {
    if (minimal) {
      return deviceId ? { deviceId: { exact: deviceId } } : true;
    }

    const constraints = {
      channelCount: 1,
      volume: 1.0,
      echoCancellation: true,
      noiseSuppression: true,
      latency: 0,
    };

    if (deviceId) {
      constraints.deviceId = { exact: deviceId };
    }

    return constraints;
  }, []);

  const requestMicrophoneStream = useCallback(
    async (deviceId = null) => {
      const attempts = [];

      if (deviceId) {
        attempts.push({
          label: "selected device with preferred constraints",
          constraints: buildAudioConstraints(deviceId),
        });
        attempts.push({
          label: "selected device with minimal constraints",
          constraints: buildAudioConstraints(deviceId, true),
        });
      }

      attempts.push({
        label: "default device with preferred constraints",
        constraints: buildAudioConstraints(),
      });
      attempts.push({
        label: "default device with minimal constraints",
        constraints: buildAudioConstraints(null, true),
      });

      let lastRecoverableError = null;

      for (const attempt of attempts) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: attempt.constraints,
          });

          return {
            stream,
            attemptLabel: attempt.label,
          };
        } catch (error) {
          if (!isRecoverableMicrophoneConstraintError(error)) {
            throw error;
          }

          lastRecoverableError = error;
          console.warn("[Microphone] getUserMedia attempt failed, retrying:", {
            attempt: attempt.label,
            name: error?.name,
            constraint: error?.constraint,
            message: error?.message,
          });
        }
      }

      throw lastRecoverableError;
    },
    [buildAudioConstraints, isRecoverableMicrophoneConstraintError]
  );

  // Unified cleanup: update state and stop recorders, tracks, and audio nodes.
  const fullCleanup = useCallback(async (options = {}) => {
    const { clearQueue = true, closeAudioContext = false } = options;
    // console.log("[Microphone] Starting cleanup");

    // Prevent worklet callbacks from enqueueing another audio frame while the
    // asynchronous cleanup below is still releasing the capture graph.
    microphoneOpenRef.current = false;

    // Manual push-to-talk finalization preserves the websocket queue. Flush
    // the sub-50ms PCM remainder into that queue before tearing down the audio
    // graph so the final word is not silently truncated.
    if (!clearQueue && audioBufferQueueRef.current.length > 0) {
      const remainder = audioBufferQueueRef.current;
      enqueueBlob(copyAudioBufferView(remainder));
    }
    audioBufferQueueRef.current = new Int16Array(0);

    setMicrophoneOpen(false);
    setIsRecordingConversation(false);

    setMicrophoneDeviceName(null);
    // setConversationChunks([]); // Don't clear conversation chunks in cleanup - let the app manage this

    // Small delay to let the state update.
    await new Promise((resolve) => setTimeout(resolve, 50));

    stopConvRecorder();
    stopAllTracks();

    if (conversationDestinationRef.current) {
      try {
        conversationDestinationRef.current.disconnect();
      } catch (e) {
        // Best-effort cleanup only.
      }
      conversationDestinationRef.current = null;
    }

    if (streamSourceRef.current) {
      try {
        streamSourceRef.current.disconnect();
      } catch (e) {
        // Best-effort cleanup only.
      }
      streamSourceRef.current = null;
    }

    if (micGainRef.current) {
      try {
        micGainRef.current.disconnect();
      } catch (e) {
        // Best-effort cleanup only.
      }
      micGainRef.current = null;
    }

    if (audioWorkletNodeRef.current) {
      try {
        audioWorkletNodeRef.current.disconnect();
      } catch (e) {
        // Best-effort cleanup only.
      }
      audioWorkletNodeRef.current = null;
    }

    if (systemSourceRef.current) {
      try {
        systemSourceRef.current.disconnect();
      } catch (e) {
        // Best-effort cleanup only.
      }

      // Always reconnect the system source to speakers so TTS audio remains
      // audible even after microphone cleanup. The source can't be recreated
      // (createMediaElementSource is one-shot), so losing the connection would
      // silence all future TTS playback until a full page reload.
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        try {
          systemSourceRef.current.connect(audioContextRef.current.destination);
        } catch (e) {
          // Best-effort cleanup only.
        }
      }
    }

    // Clear any pending audio chunks so a new recording session cannot accidentally
    // send stale audio (which can lead to transcript carry-over between UI fields).
    if (clearQueue) {
      clearQueuedAudio();
    }

    if (audioContextRef.current && closeAudioContext) {
      // If a MediaElementSource was created from the shared audio element,
      // closing the AudioContext would permanently orphan the capture and
      // leave the element unable to produce audible output on subsequent
      // visits. Suspend instead so we can resume + reuse on next mount.
      if (systemSourceRef.current) {
        try {
          if (audioContextRef.current.state === "running") {
            await audioContextRef.current.suspend();
          }
        } catch (err) {
          console.warn("[Microphone] Error suspending audio context:", err);
        }
        // Don't null out refs — they are persisted on the audio element
        // for recovery on remount.
      } else {
        try {
          if (audioContextRef.current.state !== "closed") {
            await audioContextRef.current.close();
          }
        } catch (err) {
          console.warn("[Microphone] Error closing audio context:", err);
        }
        audioContextRef.current = null;
        workletLoadedRef.current = false;
      }
    }

    if (audioDebugRef.current.noAudioTimeoutId) {
      clearTimeout(audioDebugRef.current.noAudioTimeoutId);
      audioDebugRef.current.noAudioTimeoutId = null;
    }
    audioDebugRef.current.firstAudioLogged = false;
    audioDebugRef.current.lastAudioAt = 0;
    audioDebugRef.current.chunkCount = 0;

    // console.log("[Microphone] Cleanup complete");
  }, [clearQueuedAudio, enqueueBlob, stopConvRecorder, stopAllTracks]);

  // Run cleanup on unmount.
  useEffect(() => {
    return () => {
      void fullCleanup({ closeAudioContext: true });
    };
  }, [fullCleanup]);

  // In-flight startMicrophone attempt. Concurrent callers that want the same
  // configuration join this attempt instead of failing (see startMicrophone).
  const inFlightStartRef = useRef(null);

  // Full microphone startup: stream acquisition, audio graph, and the optional
  // conversation recorder. Only entered through startMicrophone below, which
  // guarantees a single attempt runs at a time.
  const performMicrophoneStart = useCallback(
    async (recordConversation, deviceId) => {
      try {
        isInitializingRef.current = true;
        setIsInitializing(true);
        await fullCleanup();
        // Add small delay to ensure previous stream is fully released
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (typeof MediaRecorder === "undefined") {
          const error = new Error(
            "Recording is not supported in this browser. Please use the latest Chrome, Edge, or Firefox."
          );
          error.name = "NotSupportedError";
          throw error;
        }

        if (!navigator?.mediaDevices?.getUserMedia) {
          const error = new Error(
            "Microphone access is not available in this browser. Please use the latest Chrome, Edge, or Firefox."
          );
          error.name = "NotSupportedError";
          throw error;
        }

        const microphoneStart = await requestMicrophoneStream(deviceId);
        const stream = microphoneStart.stream;

        setStream(stream);

        // Get microphone device name and native sample rate
        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) {
          console.warn("[Microphone] No audio tracks available on stream");
          throw new Error("Microphone stream started without an audio track.");
        }
        if (audioTrack && audioTrack.label) {
          setMicrophoneDeviceName(audioTrack.label);
        } else {
          setMicrophoneDeviceName("Default Microphone");
        }

        // Get the actual sample rate from the track settings
        const trackSettings = audioTrack.getSettings();
        const actualDeviceId = trackSettings.deviceId || null;
        const nativeSampleRate = trackSettings.sampleRate || 48000; // Default to 48000 if not available

        try {
          // Open microphone state.
          setMicrophoneOpen(true);

          // Use the microphone's native sample rate for the AudioContext
          const AudioContextConstructor =
            window.AudioContext || window.webkitAudioContext;
          if (!AudioContextConstructor) {
            const error = new Error(
              "Audio processing is not supported in this browser. Please use the latest Chrome, Edge, or Firefox."
            );
            error.name = "NotSupportedError";
            throw error;
          }

          if (
            !audioContextRef.current ||
            audioContextRef.current.state === "closed"
          ) {
            audioContextRef.current = new AudioContextConstructor({
              sampleRate: nativeSampleRate,
              latencyHint: "balanced",
            });
            workletLoadedRef.current = false;
            // If the AudioContext was recreated, the old systemSource is dead
            systemSourceRef.current = null;
          }

          if (audioContextRef.current.state !== "running") {
            await audioContextRef.current.resume();
          }
          // console.log("[Microphone] Creating audio source");
          const source =
            audioContextRef.current.createMediaStreamSource(stream);
          streamSourceRef.current = source;

          // Create a gain node for the microphone source
          const micGain = audioContextRef.current.createGain();
          micGain.gain.value = 1.0;
          micGainRef.current = micGain;

          if (typeof AudioWorkletNode === "undefined") {
            const error = new Error(
              "Real-time microphone processing is not supported in this browser. Please use the latest Chrome, Edge, or Firefox."
            );
            error.name = "NotSupportedError";
            throw error;
          }

          const audioWorkletUrl = "/audio-processor.js";
          if (!workletLoadedRef.current) {
            try {
              await audioContextRef.current.audioWorklet.addModule(
                audioWorkletUrl
              );
              workletLoadedRef.current = true;
            } catch (error) {
              console.warn(
                "[Microphone] Audio worklet module load failed:",
                error
              );
              const notSupportedError = new Error(
                "Real-time microphone processing is not supported in this browser. Please use the latest Chrome, Edge, or Firefox."
              );
              notSupportedError.name = "NotSupportedError";
              throw notSupportedError;
            }
          }

          // Validate AudioContext is still a valid BaseAudioContext before
          // constructing AudioWorkletNode. It can become invalid if cleanup
          // runs concurrently (e.g., user navigates away during init).
          if (
            !audioContextRef.current ||
            !(audioContextRef.current instanceof BaseAudioContext) ||
            audioContextRef.current.state === "closed"
          ) {
            throw new Error(
              "AudioContext was closed during microphone initialization. Please try again."
            );
          }

          try {
            audioWorkletNodeRef.current = new AudioWorkletNode(
              audioContextRef.current,
              "audio-processor"
            );
          } catch (error) {
            // Handle the case where the context became invalid between check and use
            if (
              error instanceof TypeError &&
              error.message?.includes("BaseAudioContext")
            ) {
              console.warn(
                "[Microphone] AudioContext invalidated during worklet creation:",
                error
              );
              throw new Error(
                "Microphone setup was interrupted. Please try again."
              );
            }

            const shouldRetry =
              error?.name === "InvalidStateError" &&
              error?.message?.includes("audio-processor");
            if (!shouldRetry) {
              console.error(
                "[Microphone] Audio worklet node creation failed:",
                error
              );
              throw error;
            }

            const cacheBustedUrl = `${audioWorkletUrl}?ts=${Date.now()}`;
            console.warn(
              "[Microphone] Audio worklet node not registered; retrying addModule:",
              cacheBustedUrl
            );
            await audioContextRef.current.audioWorklet.addModule(
              cacheBustedUrl
            );
            workletLoadedRef.current = true;
            audioWorkletNodeRef.current = new AudioWorkletNode(
              audioContextRef.current,
              "audio-processor"
            );
          }

          // Send the native sample rate to the audio processor for resampling
          audioWorkletNodeRef.current.port.postMessage({
            type: "setSampleRate",
            nativeSampleRate: audioContextRef.current.sampleRate,
            targetSampleRate: 16000,
          });

          // Attach the onmessage callback. Note that we use the ref (microphoneOpenRef)
          // so that the callback always sees the current state.
          audioWorkletNodeRef.current.port.onmessage = (event) => {
            try {
              // Check if component is still mounted
              if (!microphoneOpenRef.current) {
                return;
              }
              // Check if audio context is valid and running
              if (
                !audioContextRef.current ||
                audioContextRef.current.state !== "running"
              ) {
                return;
              }
              // Check if worklet node is still connected
              if (!audioWorkletNodeRef.current) {
                return;
              }
              const now = Date.now();
              if (!event.data.audio_data) {
                audioDebugRef.current.lastNoDataLogAt = now;
                return;
              }

              const currentBuffer = new Int16Array(event.data.audio_data);
              if (!currentBuffer.length) {
                return;
              }
              audioDebugRef.current.lastAudioAt = now;
              audioDebugRef.current.chunkCount += 1;

              audioDebugRef.current.firstAudioLogged = true;

              // Process the audio without causing re-renders.
              const newBuffer = mergeBuffers(
                audioBufferQueueRef.current,
                currentBuffer
              );
              audioBufferQueueRef.current = newBuffer;

              // Use 16000 Hz for buffer duration calculation since audio is resampled
              const targetSampleRate = 16000;
              const bufferDuration =
                (audioBufferQueueRef.current.length / targetSampleRate) * 1000;

              // Reduced buffer duration from 100ms to 50ms for lower latency
              if (bufferDuration >= 50) {
                const totalSamples = Math.floor(targetSampleRate * 0.05);
                // Keep as Int16Array for Assembly AI - don't convert to Uint8Array
                const finalBuffer = audioBufferQueueRef.current.subarray(
                  0,
                  totalSamples
                );
                audioBufferQueueRef.current =
                  audioBufferQueueRef.current.subarray(totalSamples);

                // Send raw Int16Array buffer to Assembly AI
                if (microphoneOpenRef.current) {
                  // IMPORTANT: finalBuffer is a subarray view into a larger ArrayBuffer.
                  // `finalBuffer.buffer` returns the ENTIRE underlying ArrayBuffer.
                  // We must slice the exact byte range (byteOffset..byteOffset+byteLength),
                  // otherwise we can send extra audio (including adjacent/future samples)
                  // and cause duplication/transcript contamination across turns/fields.
                  const chunk = copyAudioBufferView(finalBuffer);
                  enqueueBlob(chunk);
                  audioDebugRef.current.lastChunkLogAt = now;
                }
              }
            } catch (error) {
              console.error(
                "[Microphone] Error processing audio message:",
                error
              );
            }
          };

          // Connect source to both gain and worklet for processing
          source.connect(micGain);
          source.connect(audioWorkletNodeRef.current);
          audioWorkletNodeRef.current.connect(
            audioContextRef.current.destination
          );

          audioDebugRef.current.noAudioTimeoutId = setTimeout(() => {
            if (!audioDebugRef.current.firstAudioLogged) {
              console.warn("[Microphone] No audio chunks received after startup", {
                requestedDeviceId: deviceId || null,
                actualDeviceId,
                deviceLabel: audioTrack.label || "Default Microphone",
                attemptLabel: microphoneStart.attemptLabel,
              });
            }
          }, 2000);
          await new Promise((resolve) => setTimeout(resolve, 100));

          if (recordConversation) {
            // Create destination stream for conversation recording
            const destination =
              audioContextRef.current.createMediaStreamDestination();
            conversationDestinationRef.current = destination;

            // Connect the microphone gain to destination for user voice recording
            micGain.connect(destination);

            // Set up system audio connection for persona/customer voice.
            // createMediaElementSource permanently captures the element — it can
            // only be called once per element across its entire lifetime.  We
            // persist the AudioContext + source on the DOM node so they survive
            // component unmount/remount and prevent orphaned captures.
            const audioElement =
              document.getElementById("shared-audio-player");
            if (audioElement) {
              try {
                if (!systemSourceRef.current) {
                  systemSourceRef.current =
                    audioContextRef.current.createMediaElementSource(
                      audioElement
                    );
                }

                // Persist on the element for recovery after unmount
                audioElement.__persistedAudioCtx = audioContextRef.current;
                audioElement.__persistedSystemSource = systemSourceRef.current;
                audioElement.__persistedWorkletLoaded = workletLoadedRef.current;

                systemSourceRef.current.disconnect();
                systemSourceRef.current.connect(destination);
                systemSourceRef.current.connect(
                  audioContextRef.current.destination
                );
              } catch (error) {
                console.warn(
                  "[Microphone] System audio connection error:",
                  error
                );
              }
            }

            const mimeType = getSupportedMimeType();
            setChosenMimeType(mimeType);

            startConvRecorder(destination.stream, 1000, { mimeType }, (e) => {
              if (e.data.size > 0) {
                setConversationChunks((prev) => [...prev, e.data]);
              }
            });
            setIsRecordingConversation(true);
          }

          return {
            deviceId: actualDeviceId,
            attemptLabel: microphoneStart.attemptLabel,
          };
        } catch (error) {
          console.error("[Microphone] Setup error:", error);
          throw error;
        }
      } catch (error) {
        fullCleanup();
        throw error;
      } finally {
        isInitializingRef.current = false;
        setIsInitializing(false);
      }
    },
    [
      fullCleanup,
      startConvRecorder,
      enqueueBlob,
      setStream,
      mergeBuffers,
      requestMicrophoneStream,
    ]
  );

  // Start the microphone and set up audio processing. Concurrent calls with
  // the same configuration join the in-flight attempt; a mismatched
  // configuration gets MicrophoneBusyError (SAL-2803, see
  // lib/microphone/start-coalescing.js).
  const startMicrophone = useCallback(
    async (recordConversation = false, deviceId = null) =>
      coalesceMicrophoneStart(
        inFlightStartRef,
        recordConversation,
        deviceId,
        performMicrophoneStart
      ),
    [performMicrophoneStart]
  );

  const stopMicrophone = useCallback((options = {}) => {
    // Immediately set recording state to false before cleanup
    setIsRecordingConversation(false);

    return fullCleanup(options);
  }, [fullCleanup]);

  const startConversationRecording = useCallback(async () => {
    try {
      setConversationChunks([]);
      await startMicrophone(true);
    } catch (error) {
      console.error("Error startConversationRecording:", error);
    }
  }, [startMicrophone]);

  const stopConversationRecording = useCallback(() => {
    stopConvRecorder();
    setIsRecordingConversation(false);
  }, [stopConvRecorder]);

  const clearConversationChunks = useCallback(() => {
    setConversationChunks([]);
  }, []);

  const buildConversationAudioPayload = useCallback(() => {
    if (!conversationChunks.length || !chosenMimeType) {
      return null;
    }

    const blob = new Blob(conversationChunks, { type: chosenMimeType });
    let extension = "webm";
    if (chosenMimeType.includes("mp4")) extension = "mp4";
    else if (chosenMimeType.includes("ogg")) extension = "ogg";
    else if (chosenMimeType.includes("wav")) extension = "wav";

    return {
      blob,
      extension,
      mimeType: chosenMimeType,
      filename: `conversation.${extension}`,
    };
  }, [conversationChunks, chosenMimeType]);

  const getAudioUrl = useCallback(async () => {
    const audioPayload = buildConversationAudioPayload();
    if (!audioPayload) {
      return null;
    }

    try {
      return await uploadAudioBlobToS3({
        blob: audioPayload.blob,
        mimeType: audioPayload.mimeType,
        filename: audioPayload.filename,
      });
    } catch (error) {
      try {
        if (typeof window !== "undefined") {
          const simulationType = resolveSimulationTypeFromPath(
            window.location.pathname
          );
          if (simulationType) {
            await enqueuePendingSimulationAudio({
              simulationType,
              blob: audioPayload.blob,
              mimeType: audioPayload.mimeType,
              sourcePath: `${window.location.pathname}${window.location.search}`,
            });
            console.warn("[Microphone] Queued pending audio upload fallback", {
              simulationType,
              mimeType: audioPayload.mimeType,
              size: audioPayload.blob.size,
            });
          }
        }
      } catch (queueError) {
        console.error(
          "[Microphone] Failed to queue pending audio upload fallback:",
          queueError
        );
      }

      console.error("Error uploading recording to S3:", error);
      throw error;
    }
  }, [buildConversationAudioPayload]);

  const getConversationDuration = useCallback(async () => {
    if (typeof window === "undefined") {
      return null;
    }

    if (!conversationChunks.length || !chosenMimeType) {
      return null;
    }

    try {
      const blob = new Blob(conversationChunks, { type: chosenMimeType });
      const arrayBuffer = await blob.arrayBuffer();
      const AudioContextConstructor =
        window.AudioContext || window.webkitAudioContext;

      if (!AudioContextConstructor) {
        console.warn("[Microphone] AudioContext unavailable for duration calc");
        return null;
      }

      const tempAudioContext = new AudioContextConstructor();

      try {
        const audioBuffer = await new Promise((resolve, reject) => {
          tempAudioContext.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
        });

        return audioBuffer?.duration ?? null;
      } finally {
        try {
          if (tempAudioContext.state !== "closed") {
            await tempAudioContext.close();
          }
        } catch (closeError) {
          console.warn(
            "[Microphone] Error closing temporary AudioContext:",
            closeError
          );
        }
      }
    } catch (error) {
      console.error("[Microphone] Error calculating audio duration:", error);
      return null;
    }
  }, [conversationChunks, chosenMimeType]);

  const getSupportedMimeType = useCallback(() => {
    // Prioritize explicit codec specifications to ensure consistent WebM encoding
    const preferredMimeTypes = [
      "audio/webm;codecs=opus", // Explicit Opus codec - most compatible
      "audio/ogg;codecs=opus", // Fallback to OGG with Opus
      "audio/mp4", // Fallback to MP4
      "audio/webm", // Last resort - avoid if possible due to codec ambiguity
    ];

    const supportedType = preferredMimeTypes.find(
      MediaRecorder.isTypeSupported
    );

    return supportedType;
  }, []);

  // Memoize the context value to prevent unnecessary updates.
  const contextValue = useMemo(
    () => ({
      microphone: getStream(),
      microphoneOpen,
      startMicrophone,
      stopMicrophone,
      enqueueBlob,
      removeBlob,
      clearQueuedAudio,
      getAudioUrl,
      firstBlob,
      queueSize,
      queue,
      startConversationRecording,
      stopConversationRecording,
      clearConversationChunks,
      isRecordingConversation,
      stream: getStream(),
      microphoneDeviceName,
      getConversationDuration,
    }),
    [
      getStream,
      microphoneOpen,
      startMicrophone,
      stopMicrophone,
      enqueueBlob,
      removeBlob,
      clearQueuedAudio,
      getAudioUrl,
      firstBlob,
      queueSize,
      queue,
      startConversationRecording,
      stopConversationRecording,
      clearConversationChunks,
      isRecordingConversation,
      microphoneDeviceName,
      getConversationDuration,
    ]
  );

  return (
    <MicrophoneContext.Provider value={contextValue}>
      {children}
    </MicrophoneContext.Provider>
  );
};

const useMicrophone = () => {
  const context = useContext(MicrophoneContext);
  if (!context) {
    throw new Error("useMicrophone must be used within a MicrophoneProvider");
  }
  return context;
};

export { MicrophoneContextProvider, useMicrophone };
